import * as z from 'zod';
import { Gender } from 'enum/gender.enum';
import { USER_DIVERSITY } from 'enum/diversity.enum';
import { Affiliation } from 'enum/affiliation.enum';
import { Classification } from 'enum/classification.enum';
import { USER_DEPARTMENT } from 'enum/department.enum';
import { USER_DEPARTMENT_ROLES_MAP } from 'enum/department-role.enum';

import { Month, MONTHS } from '@app/util';
import { ErrorMessages } from './error-messages';

export const signUpDetailsFormSchema = z.object({
  zipCode: z.string().regex(/^\d{5}$/, ErrorMessages.ZipCode.INVALID),
  mobileNumber: z
    .string()
    .regex(/^[2-9]\d{9}$/, ErrorMessages.PhoneNumber.INVALID),
});

export type SignUpDetailsFormData = z.TypeOf<typeof signUpDetailsFormSchema>;

// IDENTIFICATION
export const signUpIdentificationFormSchema = z.object({
  month: z.nativeEnum(MONTHS as Record<number, Month>, {
    errorMap: () => ({ message: ErrorMessages.Month.INVALID }),
  }),
  day: z
    .number()
    .min(1, ErrorMessages.Day.INVALID)
    .max(31, ErrorMessages.Day.INVALID)
    .refine(x => !!x, { message: ErrorMessages.Day.INVALID }),
  year: z
    .number()
    .min(1950, ErrorMessages.GradYear.INVALID)
    .max(2100, ErrorMessages.GradYear.INVALID)
    .refine(x => !!x, { message: ErrorMessages.GradYear.INVALID }),

  gender: z.nativeEnum(Gender, {
    errorMap: () => ({ message: ErrorMessages.Gender.INVALID }),
  }),
});

export type SignUpIdentificationFormData = z.TypeOf<
  typeof signUpIdentificationFormSchema
>;

// DIVERSITY
export const signUpDiversityFormSchema = z.object({
  diversity: z
    .array(z.string())
    .min(1, { message: ErrorMessages.Diversity.INVALID })
    .refine(
      selectedDiversity =>
        selectedDiversity.every(diversity =>
          USER_DIVERSITY.includes(diversity),
        ),
      { message: ErrorMessages.Diversity.INVALID },
    ),
});

export type SignUpDiversityFormData = z.TypeOf<
  typeof signUpDiversityFormSchema
>;

// AFFILIATION
export const signUpAffiliationFormSchema = z
  .object({
    affiliation: z.nativeEnum(Affiliation, {
      errorMap: () => ({ message: ErrorMessages.Affiliation.INVALID }),
    }),
    studentId: z.string().optional().nullable(),
    otherAffiliationDetail: z.string().optional().nullable(),
  })
  .superRefine(
    ({ affiliation, studentId, otherAffiliationDetail }, refinementContext) => {
      if (affiliation === Affiliation['College Student'] && !studentId) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.StudentId.INVALID,
          path: ['studentId'],
        });
      }
      if (affiliation === Affiliation.Other && !otherAffiliationDetail) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.OtherAffiliationDetail.INVALID,
          path: ['otherAffiliationDetail'],
        });
      }
      return true;
    },
  );

export type SignUpAffiliationFormData = z.TypeOf<
  typeof signUpAffiliationFormSchema
>;

// JOURNEY
export const signUpJourneyFormSchema = z.object({
  journey: z.nativeEnum(Classification, {
    errorMap: () => ({ message: ErrorMessages.Journey.INVALID }),
  }),
  graduationYear: z
    .number()
    .min(1900, ErrorMessages.GraduationYear.INVALID)
    .max(2100, ErrorMessages.GraduationYear.INVALID)
    .refine(x => !!x, { message: ErrorMessages.GraduationYear.INVALID }),
});

export type SignUpJourneyFormData = z.TypeOf<typeof signUpJourneyFormSchema>;

// FOCUS — allowed names from API (school mapping or full catalog)
export const getSingUpFocusFormSchema = (allowedMajorNames: string[]) =>
  z.object({
    gpa: z.union([
      z
        .string()
        .refine(val => val !== '', { message: ErrorMessages.Gpa.INVALID }),
      z
        .number()
        .min(0, ErrorMessages.Gpa.INVALID)
        .max(4, ErrorMessages.Gpa.INVALID),
    ]),

    majors: z
      .array(z.string())
      .min(1, { message: ErrorMessages.Major.INVALID })
      .refine(
        selectedMajors =>
          allowedMajorNames.length > 0 &&
          selectedMajors.every(major => allowedMajorNames.includes(major)),
        { message: 'Invalid major selected.' },
      ),
  });

export type SignupFocusFormData = z.infer<
  ReturnType<typeof getSingUpFocusFormSchema>
>;

// ROLE
export const signUpRoleFormSchema = z
  .object({
    // Make sure the value is a valid department
    department: z.string(),

    departmentRole: z.string(),

    departmentRoleOther: z.string().optional().nullable(),

    planToUseCampusLush: z
      .string()
      .min(1)
      .max(255)
      .refine(val => val !== '', {
        message: ErrorMessages.PlanToUseCampusLush.INVALID,
      }),
  })
  .superRefine(
    (
      { department, departmentRole, departmentRoleOther },
      refinementContext,
    ) => {
      if (!USER_DEPARTMENT.includes(department)) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.Department.INVALID,
          path: ['department'],
        });
      }
      if (
        department &&
        !USER_DEPARTMENT_ROLES_MAP[department].includes(departmentRole)
      ) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.Role.INVALID,
          path: ['departmentRole'],
        });
      }
      if (departmentRole === 'Other' && !departmentRoleOther) {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.Role.INVALID,
          path: ['departmentRoleOther'],
        });
      }
      return true;
    },
  );

export type SignUpRoleFormData = z.TypeOf<typeof signUpRoleFormSchema>;
